import cloneDeep from "lodash/cloneDeep";
import { ISettingsProps } from "./Models.ts";
import { bucketFor, isTimesTableSheet, operationsOf } from "./FactRanges.ts";
import { ladderForSettings, levelApplies, levelOf } from "./FactLevels.ts";
import {
    DoubleDigitNumbers,
    DecimalMaxWholeOptions,
    DecimalPlacesOptions,
    MaxDenominatorOptions,
    FractionModelMaxDenominatorOptions,
    isFractionModelQuestion,
    isRationalQuestion,
    ignoresNumberPropertyMaxValue,
    isScientificQuestion,
    RationalKindOptions,
    MoneyRangeOptions,
    OrderOfOperationsLevelOptions,
    PlaceValueDigitOptions,
    ProblemFormatOptions,
    ROMAN_MAX_OPTIONS,
    MAGIC_SQUARE_MAX_OPTIONS,
    SingleDigitNumbers,
    TimesTableNumbers,
    TimeIntervalOptions,
    TripleDigitNumbers,
    AVERAGE_LIST_LENGTH_OPTIONS,
    BAR_GRAPH_MAX_OPTIONS,
    GeometryAngleStepOptions,
    LinePlotScaleOptions,
    CoordinateGridOptions,
    CoordinateQuestionOptions,
    CalendarSpanOptions,
    VolumeQuestionOptions,
    ShapeQuestionOptions,
    VOLUME_MAX_EDGE_OPTIONS,
    CIRCLE_MAX_RADIUS_OPTIONS,
    PYTHAGOREAN_MAX_SIDE_OPTIONS,
} from "./Constants.ts";
import { asksBeforeAfter } from "./CalendarGenerator.ts";

export type StepDirection = -1 | 1;

export interface DifferentiationTier {
    settings: ISettingsProps;
    /** 'down' | 'on' | 'up' — for deriving the seed; only 'on' uses the base seed. */
    role: 'down' | 'on' | 'up';
}

export interface DifferentiationPlan {
    /** Ordered easiest-first: [down?, on, up?]. */
    tiers: DifferentiationTier[];
    /** True when a tier could not be produced because the settings are at an edge. */
    floorReached: boolean;
    ceilingReached: boolean;
}

/**
 * The tiers of a differentiation set for these settings: one step easier (if
 * possible), the settings as given, one step harder (if possible). The middle
 * tier is the settings unchanged, so it is byte-identical to the ordinary
 * single sheet at the same seed (ADR 0010 §2).
 */
export function differentiationPlan(settings: ISettingsProps): DifferentiationPlan {
    const down = step(settings, -1);
    const up = step(settings, 1);
    const tiers: DifferentiationTier[] = [];
    if (down) tiers.push({ settings: down, role: 'down' });
    tiers.push({ settings, role: 'on' });
    if (up) tiers.push({ settings: up, role: 'up' });
    return { tiers, floorReached: !down, ceilingReached: !up };
}

/** The seed a tier draws from: the base seed on-level, a derived seed either side. */
export function tierSeed(baseSeed: string, role: DifferentiationTier['role']): string {
    if (role === 'on') return baseSeed;
    return role === 'down' ? `${baseSeed}:d` : `${baseSeed}:u`;
}

/**
 * One step easier (-1) or harder (+1) than the given settings, or `null` when
 * the settings are already at that edge for their format.
 *
 * "One step" means something different per format, so each format defines its
 * own — see the table in ADR 0010 §3. A format with no branch here falls
 * through to `null`, which the differentiation set reports honestly rather than
 * printing a duplicate tier (ADR 0010 §4).
 *
 * Pure: no context, no React, no mutation of the input.
 */
export function step(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    switch (settings.Format) {
        case ProblemFormatOptions.MathFacts:
            // A level test steps its level, not its ranges, which it ignores
            // (ADR 0071 §7). levelApplies is false on the formats below.
            if (levelApplies(settings)) {
                return stepFactLevel(settings, direction);
            }
            return stepWholeNumberRanges(settings, direction);
        case ProblemFormatOptions.WordProblem:
        case ProblemFormatOptions.RiddleDecoder: // the same facts, a different page (ADR 0088 §3)
        case ProblemFormatOptions.ColorByNumber: // same operand ladder; the picture is fixed (ADR 0058)
            return stepWholeNumberRanges(settings, direction);
        case ProblemFormatOptions.Fractions:
            // Denominator size is the dominant difficulty axis and the only one
            // that steps cleanly in both directions with well-defined edges.
            // Like vs. unlike is left to the teacher (ADR 0010 §3 lists it, but
            // combining the two axes makes a step ambiguous and irreversible).
            //
            // The two model questions (ADR 0043 §1) read their own denominator
            // bound, so stepping `MaxDenominator` for them would move a setting
            // they never consult and hand back three identical tiers - which is
            // worse than ADR 0010 §4's honest `null`, because it looks like it
            // worked. Their own ladder is the same difficulty axis and steps
            // just as cleanly.
            if (isFractionModelQuestion(settings.FractionQuestion)) {
                return stepOption(
                    settings,
                    direction,
                    "FractionModelMaxDenominator",
                    FractionModelMaxDenominatorOptions
                );
            }
            return stepOption(settings, direction, "MaxDenominator", MaxDenominatorOptions);
        case ProblemFormatOptions.Money:
            return stepOption(settings, direction, "MoneyMaxDollars",
                MoneyRangeOptions.map((entry) => entry.maxDollars));
        case ProblemFormatOptions.Decimals:
            return stepDecimals(settings, direction);
        case ProblemFormatOptions.PlaceValue:
            // The scientific-notation types have no digit count to step (ADR 0130 §1).
            if (isScientificQuestion(settings.PlaceValueQuestion)) {
                return null;
            }
            return stepOption(settings, direction, "PlaceValueDigits", PlaceValueDigitOptions);
        case ProblemFormatOptions.TellingTime:
            return stepTellingTime(settings, direction);
        case ProblemFormatOptions.OrderOfOperations:
            return stepOrderOfOperations(settings, direction);
        case ProblemFormatOptions.Algebra:
            return stepAlgebraMaxValue(settings, direction);
        case ProblemFormatOptions.Integers:
            return stepIntegerMaxValue(settings, direction);
        case ProblemFormatOptions.NumberProperties:
            return stepNumberPropertyMaxValue(settings, direction);
        case ProblemFormatOptions.NumberPatterns:
            return stepPatternLength(settings, direction);
        // How high the numerals go is the ordered dial; which way the
        // conversion runs is not (ADR 0079 §4).
        case ProblemFormatOptions.RomanNumerals:
            return stepOption(settings, direction, "RomanMax", ROMAN_MAX_OPTIONS);
        case ProblemFormatOptions.NumberLine:
            return stepNumberLineRange(settings, direction);
        case ProblemFormatOptions.Percent:
            return stepPercentMaxValue(settings, direction);
        case ProblemFormatOptions.Ratio:
            return stepRatioMaxValue(settings, direction);
        case ProblemFormatOptions.Shapes:
            return stepShapeMaxSide(settings, direction);
        // The longest edge is the dial (ADR 0095 §4). Counting has none.
        case ProblemFormatOptions.Volume:
            return stepVolumeMaxEdge(settings, direction);
        // A larger radius is a heavier multiplication (ADR 0106 §1). The
        // question type has no order, as with geometry's.
        case ProblemFormatOptions.Circles:
            return stepOption(settings, direction, "CircleMaxRadius", CIRCLE_MAX_RADIUS_OPTIONS);
        // Probability has no dial: the spinner and the bag are drawn at the
        // size that reads, and which question is asked is not an order
        // (ADR 0108 §1). Reported, not faked.
        case ProblemFormatOptions.Probability:
            return null;
        // A longer longest side is a bigger triple to square and subtract
        // (ADR 0109 §2). The question type has no order, as with circles'.
        case ProblemFormatOptions.PythagoreanTheorem:
            return stepOption(settings, direction, "PythagoreanMaxSide", PYTHAGOREAN_MAX_SIDE_OPTIONS);
        // How round the angles are is the only ordered dial geometry has
        // (ADR 0049 §6). The question type is not one - classifying an angle
        // is not easier than finding a complement, just different - which is
        // what Percent and Ratio each found for their own formats. The ladder
        // is ordered easiest first, so +1 is a finer step and harder
        // arithmetic: 180 - 130 becomes 180 - 137.
        case ProblemFormatOptions.Geometry:
            return stepOption(settings, direction, "GeometryAngleStep", GeometryAngleStepOptions);
        case ProblemFormatOptions.Averages:
            return stepAverageListLength(settings, direction);
        case ProblemFormatOptions.BarGraphs:
            return stepBarGraphMaxValue(settings, direction);
        // The tick unit is the ordered dial: whole numbers, halves, quarters,
        // eighths (ADR 0084 §9). The question set and the mark are not.
        case ProblemFormatOptions.LinePlots:
            return stepLinePlotScale(settings, direction);
        // How far a before-and-after question counts (ADR 0093 §5). A sheet
        // that asks none has nothing to step, and says so.
        case ProblemFormatOptions.Calendar:
            return stepCalendarSpan(settings, direction);
        // The grid is the dial: first quadrant, then four (ADR 0085 §7).
        case ProblemFormatOptions.CoordinatePlane:
            return stepCoordinateGrid(settings, direction);
        // How big the numbers in the square go is the dial (ADR 0088 §4).
        case ProblemFormatOptions.MagicSquares:
            return stepOption(settings, direction, "MagicSquareMax", MAGIC_SQUARE_MAX_OPTIONS);
        case ProblemFormatOptions.FactFamily:
            return stepFactFamilyDigits(settings, direction);
        default:
            // Rounding & Comparing has no meaningful single difficulty axis
            // (the place and the digit count move together); reported, not faked.
            return null;
    }
}

/** One rung of the operand ladder: a digit count and the values selected. */
interface Rung {
    digits: number;
    selection: number[];
}

/**
 * A difficulty ladder for one whole-number operand, easiest first. Math Facts
 * narrows the value selection before it drops a digit — two-digit to one-digit
 * is a large jump and most classrooms need the small one (ADR 0010 §3).
 *
 * A multiplication or division sheet has one more rung: the full 0-12 grid,
 * harder than 0-9 and easier than any two-digit work (ADR 0064 §4).
 */
function operandLadder(settings: ISettingsProps): Rung[] {
    const timesTables = isTimesTableSheet(operationsOf(settings))
        ? [{ digits: 1, selection: TimesTableNumbers.Range }]
        : [];
    return [
        { digits: 1, selection: SingleDigitNumbers.LowRange },
        { digits: 1, selection: SingleDigitNumbers.Range },
        ...timesTables,
        { digits: 2, selection: DoubleDigitNumbers.LowRange },
        { digits: 2, selection: DoubleDigitNumbers.Range },
        { digits: 3, selection: TripleDigitNumbers.LowRange },
        { digits: 3, selection: TripleDigitNumbers.Range },
    ];
}

function sameValues(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    const set = new Set(a);
    return b.every((value) => set.has(value));
}

/**
 * Where an operand sits on the ladder: the rung its selection matches, or the
 * lowest rung for its digit count when it matches none.
 */
function ladderIndexOf(ladder: Rung[], digits: number, selection: number[]): number {
    const exact = ladder.findIndex(
        (rung) => rung.digits === digits && sameValues(selection, rung.selection)
    );
    return exact >= 0 ? exact : Math.max(0, ladder.findIndex((rung) => rung.digits === digits));
}

function stepWholeNumberRanges(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const next = cloneDeep(settings);
    const ladder = operandLadder(settings);
    const operations = operationsOf(settings);
    let moved = false;

    for (const range of next.NumberRanges) {
        const index = ladderIndexOf(ladder, range.NumberOfDigits, range.NumberRangeSelection);
        const target = index + direction;
        if (target < 0 || target >= ladder.length) {
            // This operand cannot move; leave it where it is.
            continue;
        }
        const { digits, selection } = ladder[target];
        range.NumberOfDigits = digits;
        range.NumberRange = [...bucketFor(digits, operations).Range];
        range.NumberRangeSelection = [...selection];
        moved = true;
    }

    return moved ? next : null;
}

function stepDecimals(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    const placeIndex = DecimalPlacesOptions.indexOf(settings.DecimalPlaces);
    const wholeIndex = DecimalMaxWholeOptions.indexOf(settings.DecimalMaxWhole);
    const placeTarget = placeIndex + direction;
    const wholeTarget = wholeIndex + direction;

    const next = cloneDeep(settings);
    let moved = false;
    if (placeIndex >= 0 && placeTarget >= 0 && placeTarget < DecimalPlacesOptions.length) {
        next.DecimalPlaces = DecimalPlacesOptions[placeTarget];
        moved = true;
    }
    if (!moved && wholeIndex >= 0 && wholeTarget >= 0 && wholeTarget < DecimalMaxWholeOptions.length) {
        next.DecimalMaxWhole = DecimalMaxWholeOptions[wholeTarget];
        moved = true;
    }
    return moved ? next : null;
}

/**
 * Steps the interval, never the question type — reading a clock and drawing
 * its hands are different skills, not difficulties (ADR 0010 §3). Coarser is
 * easier, so the ladder runs Hour → Minute and a step down moves toward Hour.
 */
const TIME_LADDER: TimeIntervalOptions[] = [
    TimeIntervalOptions.Hour,
    TimeIntervalOptions.HalfHour,
    TimeIntervalOptions.QuarterHour,
    TimeIntervalOptions.FiveMinutes,
    TimeIntervalOptions.Minute,
];

/** Easier to harder, the values ALGEBRA_MAX_VALUE_OPTIONS offers. */
const ALGEBRA_LADDER = [10, 12, 20, 50];

/** Easier to harder, the values INTEGER_MAX_VALUE_OPTIONS offers. */
const INTEGER_LADDER = [10, 12, 20, 50];

/** Easier to harder, the values NUMBER_PROPERTY_MAX_VALUE_OPTIONS offers. */
const NUMBER_PROPERTY_LADDER = [20, 50, 100, 144];

/** Easier to harder, the values PATTERN_LENGTH_OPTIONS offers. */
const PATTERN_LENGTH_LADDER = [5, 6, 8];

/** Easier to harder, the values NUMBER_LINE_RANGE_OPTIONS offers. */
const NUMBER_LINE_LADDER = [10, 20, 50, 100];

/** Easier to harder, the values PERCENT_MAX_VALUE_OPTIONS offers. */
const PERCENT_LADDER = [50, 100, 200, 500];

/** Easier to harder, the values RATIO_MAX_VALUE_OPTIONS offers. */
const RATIO_LADDER = [20, 50, 100, 144];

/** Easier to harder, the values SHAPE_MAX_SIDE_OPTIONS offers. */
const SHAPE_LADDER = [12, 20, 50, 100];

const ORDER_OF_OPS_LADDER: OrderOfOperationsLevelOptions[] = [
    OrderOfOperationsLevelOptions.TwoOperations,
    OrderOfOperationsLevelOptions.ThreeOperations,
    OrderOfOperationsLevelOptions.Parentheses,
    OrderOfOperationsLevelOptions.Exponents,
];

/**
 * A larger side makes the multiplication heavier without changing what the
 * question asks. The question type has no order - perimeter is not easier than
 * area, though the missing-side one arguably is harder than both.
 */
function stepShapeMaxSide(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    // Scale Drawing caps its sides at 12 whatever the dial says (ADR 0107 §1),
    // so a step would print the same sheet.
    if (settings.ShapeQuestion === ShapeQuestionOptions.ScaleDrawing) {
        return null;
    }
    const index = SHAPE_LADDER.indexOf(settings.ShapeMaxSide);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= SHAPE_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.ShapeMaxSide = SHAPE_LADDER[target];
    return next;
}

/**
 * A longer edge makes the multiplication heavier without changing what the
 * question asks (ADR 0095 §4). Counting the cubes has no edge dial - its edges
 * are always 2 to 5 - so a sheet that only counts has nothing to step and says
 * so, as a calendar sheet with no before-and-after question does. Two boxes
 * (ADR 0097) and Surface Area (ADR 0100) draw their edges to the dial, so they
 * step it like Edge lengths.
 */
function stepVolumeMaxEdge(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    if (settings.VolumeQuestion === VolumeQuestionOptions.CountCubes) {
        return null;
    }
    return stepOption(settings, direction, "VolumeMaxEdge", VOLUME_MAX_EDGE_OPTIONS);
}

/**
 * A longer list is harder to find the mean, median or mode of - more values
 * to add up, more to sort, more to tally. The question type has no order:
 * mean is not harder than mode, just a different calculation.
 */
function stepAverageListLength(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = AVERAGE_LIST_LENGTH_OPTIONS.indexOf(settings.AverageListLength);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= AVERAGE_LIST_LENGTH_OPTIONS.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.AverageListLength = AVERAGE_LIST_LENGTH_OPTIONS[target];
    return next;
}

/**
 * A larger scale spaces the gridlines further apart in value, which is what
 * makes reading and comparing the bars harder - the question type has no
 * order of its own (reading one bar is not harder than finding the largest,
 * just different).
 */
function stepBarGraphMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = BAR_GRAPH_MAX_OPTIONS.indexOf(settings.BarGraphMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= BAR_GRAPH_MAX_OPTIONS.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.BarGraphMaxValue = BAR_GRAPH_MAX_OPTIONS[target];
    return next;
}

/**
 * A larger value makes the multiplier bigger, which is what a ratio question
 * is actually doing. The question type has no order - simplifying is not
 * easier than a unit rate, just different. For Proportional Tables it is
 * the largest y a table may print (ADR 0104), which bounds the constant and the
 * x values. For Proportional Graphs it is the top of the y axis (ADR 0105), which
 * decides the scales a graph may count in.
 */
function stepRatioMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = RATIO_LADDER.indexOf(settings.RatioMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= RATIO_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.RatioMaxValue = RATIO_LADDER[target];
    return next;
}

/**
 * A larger base makes the arithmetic heavier without changing the percentage
 * itself, which is the only ordered dial percent has - the question type is
 * not one (converting is not "easier" than finding the percent).
 */
function stepPercentMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = PERCENT_LADDER.indexOf(settings.PercentMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= PERCENT_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.PercentMaxValue = PERCENT_LADDER[target];
    return next;
}

/**
 * A longer number line is harder to read: the ticks are closer together and
 * fewer of them carry a label, so more counting is needed to reach the marker.
 */
function stepNumberLineRange(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = NUMBER_LINE_LADDER.indexOf(settings.NumberLineRange);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= NUMBER_LINE_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.NumberLineRange = NUMBER_LINE_LADDER[target];
    return next;
}

/**
 * A longer sequence gives more terms to read the step from, but also puts the
 * gap further from an end - so length is the ordered dial. The pattern kind is
 * not one: doubling is not harder than skip counting, just different.
 */
function stepPatternLength(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = PATTERN_LENGTH_LADDER.indexOf(settings.PatternLength);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= PATTERN_LENGTH_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.PatternLength = PATTERN_LENGTH_LADDER[target];
    return next;
}

/**
 * Number properties get harder with larger numbers - a bigger number has more
 * factors to find and a less obvious GCF. The question type has no order.
 */
function stepNumberPropertyMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    // The root types (ADR 0132 §3) and the exponent rules (ADR 0136 §6) have no range to step.
    if (ignoresNumberPropertyMaxValue(settings.NumberPropertyQuestion)) {
        return null;
    }
    const index = NUMBER_PROPERTY_LADDER.indexOf(settings.NumberPropertyMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= NUMBER_PROPERTY_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.NumberPropertyMaxValue = NUMBER_PROPERTY_LADDER[target];
    return next;
}

/**
 * Integers get harder with larger magnitudes, and that is the only ordered
 * dial the format has - comparing is not "harder" than adding.
 */
function stepIntegerMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    // Fractions ignore the dial (ADR 0122 §5): stepping it would give a second
    // sheet that is no easier or harder than the first.
    if (isRationalQuestion(settings.IntegerQuestion) && settings.RationalKind === RationalKindOptions.Fractions) {
        return null;
    }
    const index = INTEGER_LADDER.indexOf(settings.IntegerMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= INTEGER_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.IntegerMaxValue = INTEGER_LADDER[target];
    return next;
}

/**
 * Algebra gets harder with larger numbers. Steps the largest value a problem
 * may use, which is the one dial the format has that has an order to it - the
 * question type does not (solving is not "harder" than evaluating).
 */
function stepAlgebraMaxValue(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = ALGEBRA_LADDER.indexOf(settings.AlgebraMaxValue);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ALGEBRA_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.AlgebraMaxValue = ALGEBRA_LADDER[target];
    return next;
}

function stepOrderOfOperations(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const index = ORDER_OF_OPS_LADDER.indexOf(settings.OrderOfOperationsLevel);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ORDER_OF_OPS_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.OrderOfOperationsLevel = ORDER_OF_OPS_LADDER[target];
    return next;
}

/**
 * A fact family gets harder with larger numbers. Steps the first operand's
 * digit count (1 ↔ 2 ↔ 3), which `partCeiling` in the generator reads.
 */
function stepFactFamilyDigits(
    settings: ISettingsProps,
    direction: StepDirection
): ISettingsProps | null {
    const digits = settings.NumberRanges?.[0]?.NumberOfDigits ?? 1;
    const target = digits + direction;
    if (target < 1 || target > 3) {
        return null;
    }
    const next = cloneDeep(settings);
    const bucket = bucketFor(target, operationsOf(settings));
    next.NumberRanges[0] = {
        ...next.NumberRanges[0],
        NumberOfDigits: target,
        NumberRange: [...bucket.Range],
        NumberRangeSelection: [...bucket.Range],
    };
    return next;
}

function stepTellingTime(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    const index = TIME_LADDER.indexOf(settings.TimeInterval);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= TIME_LADDER.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.TimeInterval = TIME_LADDER[target];
    return next;
}

/**
 * A Quadrant sheet needs four quadrants, so it has no easier tier; anything
 * else steps between the two grids (ADR 0085 §7).
 */
function stepCoordinateGrid(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    const ladder = Object.values(CoordinateGridOptions);
    const index = ladder.indexOf(settings.CoordinateGrid);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ladder.length) {
        return null;
    }
    if (
        settings.CoordinateQuestion === CoordinateQuestionOptions.Quadrant &&
        ladder[target] === CoordinateGridOptions.FirstQuadrant
    ) {
        return null;
    }
    const next = cloneDeep(settings);
    next.CoordinateGrid = ladder[target];
    return next;
}

function stepLinePlotScale(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    const ladder = Object.values(LinePlotScaleOptions);
    const index = ladder.indexOf(settings.LinePlotScale);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ladder.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.LinePlotScale = ladder[target];
    return next;
}

function stepCalendarSpan(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    if (!asksBeforeAfter(settings.CalendarQuestion)) {
        return null;
    }
    const ladder = Object.values(CalendarSpanOptions);
    const index = ladder.indexOf(settings.CalendarSpan);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ladder.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next.CalendarSpan = ladder[target];
    return next;
}

function stepOption(
    settings: ISettingsProps,
    direction: StepDirection,
    key: 'MoneyMaxDollars' | 'PlaceValueDigits' | 'RomanMax' | 'MagicSquareMax' | 'MaxDenominator' | 'FractionModelMaxDenominator' | 'GeometryAngleStep' | 'VolumeMaxEdge' | 'CircleMaxRadius' | 'PythagoreanMaxSide',
    ladder: number[]
): ISettingsProps | null {
    const index = ladder.indexOf(settings[key]);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= ladder.length) {
        return null;
    }
    const next = cloneDeep(settings);
    next[key] = ladder[target];
    return next;
}

/**
 * One level down or up the ladder (ADR 0071 §7). Level 1 has nothing easier
 * and Mixed nothing harder, and those edges collapse the set honestly, as
 * ADR 0010 §4 has every format's edges do.
 */
function stepFactLevel(settings: ISettingsProps, direction: StepDirection): ISettingsProps | null {
    const levels = ladderForSettings(settings)!.levels;
    const next = levelOf(settings)!.id + direction;
    if (next < 1 || next > levels.length) {
        return null;
    }
    const stepped = cloneDeep(settings);
    stepped.FactLevel = next;
    return stepped;
}
